import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class OkxStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  candlesWs: ReconnectingWebSocket | undefined;
  tradesWs: ReconnectingWebSocket | undefined;

  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}-${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.depth = depth;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await axios.get<{ data: { tickSz: string }[] }>(
        `https://www.okx.com/api/v5/public/instruments?instType=SPOT&instId=${this.pair}`,
      );

      let prec = 1;

      if (res.data) {
        prec = res.data.data[0].tickSz.split('.')[1].length;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('OKX ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    from,
    to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.okx];

    try {
      const result = await axios.get<{ data: string[][] }>(
        'https://www.okx.com/api/v5/market/candles',
        {
          params: {
            instId: `${base.toUpperCase()}-${quote.toUpperCase()}`,
            limit,
            bar: innerInterval,
            after: to,
            before: from,
          },
        },
      );

      return (result.data.data ?? []).map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('OKX klines error: ', error);
      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const result = await axios.get<{
        data: {
          px: string;
          sz: string;
          side: string;
          ts: string;
        }[];
      }>('https://www.okx.com/api/v5/market/trades', {
        params: {
          instId: `${base.toUpperCase()}-${quote.toUpperCase()}`,
          limit,
        },
      });

      return (
        (result.data.data ?? []).map(item => ({
          price: item.px,
          quantity: item.sz,
          timestamp: item.ts,
          side: item.side,
        })) ?? []
      );
    } catch (error) {
      console.log('OKX trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws || this.candlesWs || this.depthWs) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://ws.okx.com:8443/ws/v5/public', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const candlesWs = new ReconnectingWebSocket('wss://ws.okx.com:8443/ws/v5/business', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const depthWs = new ReconnectingWebSocket('wss://wspri.okx.com:8443/ws/v5/ipublic', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const tradesWs = new ReconnectingWebSocket('wss://ws.okx.com:8443/ws/v5/public', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;
    this.candlesWs = candlesWs;
    this.depthWs = depthWs;
    this.tradesWs = tradesWs;

    const intervalChannel = {
      60000: 'candle1m',
      300000: 'candle5m',
      900000: 'candle15m',
      3600000: 'candle1H',
      14400000: 'candle4H',
      86400000: 'candle1Dutc',
      604800000: 'candle1Wutc',
    }[interval];

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send('ping');
      }
      if (this.candlesWs) {
        this.candlesWs.send('ping');
      }
      if (this.depthWs) {
        this.depthWs.send('ping');
      }
      if (this.tradesWs) {
        this.tradesWs.send('ping');
      }
    }, 29 * 1000);

    this.ws.onopen = () => {
      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'tickers',
              instId: this.pair,
            },
          ],
        }),
      );
    };

    this.candlesWs.onopen = () => {
      candlesWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: intervalChannel,
              instId: this.pair,
            },
          ],
        }),
      );
    };

    this.depthWs.onopen = () => {
      depthWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              depth: this.depth.toString(),
              channel: 'books-grouped',
              grouping: normalizeNumber(1 / 10 ** this.selectedPrecision),
              instId: this.pair,
            },
          ],
        }),
      );
    };

    this.tradesWs.onopen = () => {
      tradesWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'trades',
              instId: this.pair,
            },
          ],
        }),
      );
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.arg.channel === 'tickers' && data.data) {
          const { vol24h, volCcy24h, high24h, low24h, open24h, last } = data.data[0];

          const change = normalizeNumber(
            ((Number(last) - Number(open24h)) / Number(open24h)) * 100,
          );

          this.onTickerUpdate({
            baseVolume: vol24h,
            change: change,
            close: last,
            high: high24h,
            last: Number(last).toFixed(this.precision),
            low: low24h,
            quoteVolume: volCcy24h,
          });
        }
      } catch (error) {
        console.log('OKX socket error: ', error);
      }
    };

    this.candlesWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.arg.channel === intervalChannel && data.data) {
          const [time, open, high, low, close, volume] = data.data[0];

          this.klineUpdate([Math.floor(Number(time) / 1000), open, high, low, close, volume]);
        }
      } catch (error) {
        console.log('OKX klines socket error: ', error);
      }
    };

    this.depthWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.arg.channel === 'books-grouped' && data.data) {
          const action = data.action as 'snapshot' | 'update';

          const responseAsks = data.data[0].asks as [string, string][];
          const responseBids = data.data[0].bids as [string, string][];

          const asks =
            action === 'snapshot'
              ? mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids =
            action === 'snapshot'
              ? mergeOrderBook(responseBids, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          this.asks = asks;
          this.bids = bids;

          this.onOrderbookUpdate(
            aggregateOrderbook(
              asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.selectedPrecision,
            ),
          );
        }
      } catch (error) {
        console.log('OKX depth socket error: ', error);
      }
    };

    this.tradesWs.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.arg.channel === 'trades' && data.data) {
          data.data.forEach(
            ({ px, sz, side, ts }: { px: string; sz: string; side: string; ts: string }) => {
              this.onTradeUpdate({
                price: px,
                quantity: sz,
                timestamp: ts,
                side,
              });
            },
          );
        }
      } catch (error) {
        console.log('OKX trades socket error: ', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.candlesWs) {
      this.candlesWs.close();
    }
    if (this.depthWs) {
      this.depthWs.close();
    }
    if (this.tradesWs) {
      this.tradesWs.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.depthWs) {
      this.depthWs.send(
        JSON.stringify({
          op: 'unsubscribe',
          args: [
            {
              channel: 'books-grouped',
              grouping: normalizeNumber(1 / 10 ** this.selectedPrecision),
              instId: this.pair,
            },
          ],
        }),
      );

      this.selectedPrecision = tick;

      this.depthWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'books-grouped',
              grouping: normalizeNumber(1 / 10 ** this.selectedPrecision),
              instId: this.pair,
            },
          ],
        }),
      );
    }
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new OkxStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
